/**
 * PURE. `lookFromAppearance(dna, sceneShadowTint)` → the art §3.2 `CreatureLook` uniform (33 × vec4 = 528 B, linear
 * colours), following the creatures §B8.2 mapping. `lookColors()` returns the same data as a struct (previews, the lite
 * mode, portraits, tests).
 *
 * Layout (vec4 index): 0–2 rampBase, 3–5 rampStripe, 6–8 rampBelly, 9–11 rampFlesh, 12–23 rampDye[4×3], 24 strokeInk,
 * 25 iris, 26 pattern (u32), 27 patParams, 28 headFrame, 29 line, 30 fx, 31 face, 32 fxColor.
 */
import { CREATURE_LOOK_BYTES, type CreatureDNA } from '../../shared';
import { FUR_PALETTE_IDS, PALETTE_INHERIT, type FurPaletteId } from '../../content/creature/parts';
import { BARE_COLOR, DYES, FLESH, FUR_PALETTES, IRIS, SHADOW_TINTS, TINCTURES, VERDACCIO, type FurPalette } from '../../content/creature/palettes';
import { resolveSpec, type CreatureSpec } from '../dna/resolve';
import type { BuiltRig } from '../rig/skeleton';
import { deriveRamp, flatRamp, hexToSrgb, srgbToLinear, type RGB, type Ramp, type ShadowTint } from './color';

export type ShadowTintInput = keyof typeof SHADOW_TINTS | ShadowTint | [number, number, number];

/** Pattern frequencies per art §2.6 (per H) and their default warp/threshold. */
const PATTERN_DEFAULTS: readonly [number, number, number][] = [
  [0, 0, 0.5], [9, 0.35, 0.62], [7, 0.5, 0.62], [14, 0.2, 0.28], [0, 0, 0.5], [0, 0, 0.5], [0, 0, 0.5], [5, 0.4, 0.5], [5, 0.4, 0.5],
  [8, 0, 0.5], [6, 0, 0.5], [6, 0.3, 0.5], [4, 0.3, 0.5], [6, 0, 0.5], [16, 0.2, 0.3], [10, 0, 0.5],
];

export interface LookColors {
  base: Ramp; stripe: Ramp; belly: Ramp; flesh: Ramp; dyes: [Ramp, Ramp, Ramp, Ramp];
  /** Linear stroke ink + coverage. */
  stroke: RGB; cov: number;
  iris: RGB; iris2: RGB | null; catchlight: boolean;
  pattern: { type: number; seed: number; flags: number; freq: number; warp: number; threshold: number; bellyWidth: number; contrast: number };
  /** Second calico stripe (sRGB hex) when the base palette has one. */
  stripe2: string | null;
  outline: number; patina: number;
  palette: { base: FurPaletteId; stripe: FurPaletteId; belly: FurPaletteId };
}

function resolveTint(t: ShadowTintInput): ShadowTint {
  if (typeof t === 'string') return SHADOW_TINTS[t];
  if (Array.isArray(t)) {
    const hex = '#' + t.map((x) => Math.round(Math.max(0, Math.min(1, x)) * 255).toString(16).padStart(2, '0')).join('');
    return { hex, k: 0.22 };
  }
  return t;
}

const palAt = (i: number): FurPaletteId => FUR_PALETTE_IDS[i] ?? 'ochreFur';

/** Resolved creature colours (art §2.3–§2.8; creatures §B8.2). */
export function lookColors(specOrDna: CreatureSpec | CreatureDNA, tintIn: ShadowTintInput = 'arena'): LookColors {
  const spec = 'o' in specOrDna ? specOrDna : resolveSpec(specOrDna);
  const f = spec.f;
  const tint = resolveTint(tintIn);
  const baseId = palAt(f.furBase);
  const basePal: FurPalette = FUR_PALETTES[baseId];
  const stripeId = f.furStripe === PALETTE_INHERIT || f.furStripe >= FUR_PALETTE_IDS.length ? baseId : palAt(f.furStripe);
  const bellyId = f.furBelly === PALETTE_INHERIT || f.furBelly >= FUR_PALETTE_IDS.length ? baseId : palAt(f.furBelly);
  const stripePal = FUR_PALETTES[stripeId], bellyPal = FUR_PALETTES[bellyId];
  const stripeHex = stripeId === baseId ? basePal.stripe : stripePal.stripe;
  const bellyHex = bellyId === baseId ? basePal.belly : bellyPal.belly;
  const gold = baseId === 'goldLeafFur';
  const base = gold ? flatRamp(basePal.base) : deriveRamp(basePal.base, tint);
  const verd: ShadowTint = { hex: VERDACCIO, k: 0.4 };
  const fleshDef = FLESH[f.flesh] ?? FLESH[0];
  const bare = BARE_COLOR[spec.head.id] ?? BARE_COLOR[spec.body.id];
  const flesh = bare ? deriveRamp(bare, tint) : deriveRamp(fleshDef.mid, tint, fleshDef.tint === 'verdaccio' ? verd : undefined);
  const dyeHex = (i: number): string => DYES[i] ?? '#F1E4C3';
  const dyes: [Ramp, Ramp, Ramp, Ramp] = [deriveRamp(dyeHex(f.dye0), tint), deriveRamp(dyeHex(f.dye1), tint),
    f.tinctA === 0 ? flatRamp(TINCTURES[0]) : deriveRamp(TINCTURES[f.tinctA] ?? TINCTURES[1], tint),
    f.tinctB === 0 ? flatRamp(TINCTURES[0]) : deriveRamp(TINCTURES[f.tinctB] ?? TINCTURES[2], tint)];
  const pat = Math.min(15, f.pattern);
  const [freq, warp, th] = PATTERN_DEFAULTS[pat] ?? [0, 0, 0.5];
  const tabbyM = (pat === 1 || pat === 2 || spec.o.mark1 === 'tabbyM' || spec.o.mark2 === 'tabbyM') ? 1 : 0;
  const flags = tabbyM | ((f.catchlight ? 1 : 0) << 1);
  return {
    base, stripe: deriveRamp(stripeHex, tint), belly: deriveRamp(bellyHex, tint), flesh, dyes,
    stroke: srgbToLinear(hexToSrgb(stripePal.stroke)), cov: Math.max(0, Math.min(1, stripePal.cov + spec.v.furStroke)),
    iris: srgbToLinear(hexToSrgb(IRIS[f.iris] ?? IRIS[0])), iris2: f.hetero ? srgbToLinear(hexToSrgb(IRIS[f.iris2] ?? IRIS[0])) : null, catchlight: !!f.catchlight,
    pattern: { type: pat, seed: spec.dna.soulSeed, flags, freq: freq * spec.v.patScale, warp, threshold: th + (0.7 - spec.v.patContrast) * 0.3,
      bellyWidth: 0.13 + spec.v.whiteAmt * 0.25, contrast: spec.v.patContrast },
    stripe2: basePal.stripe2 ?? null, outline: spec.v.outline, patina: spec.v.patina,
    palette: { base: baseId, stripe: stripeId, belly: bellyId },
  };
}

/**
 * The art §3.2 `CreatureLook` bytes (528 B). `built` supplies the head frame (rest head centre & radius); without it a
 * canonical head frame is used.
 */
export function lookFromAppearance(dna: CreatureDNA, tint: ShadowTintInput = 'arena', built?: BuiltRig): ArrayBuffer {
  const spec = resolveSpec(dna);
  const c = lookColors(spec, tint);
  const buf = new ArrayBuffer(CREATURE_LOOK_BYTES);
  const fl = new Float32Array(buf);
  const u32 = new Uint32Array(buf);
  const put = (vec: number, rgb: RGB, a = 1): void => { fl[vec * 4] = rgb[0]; fl[vec * 4 + 1] = rgb[1]; fl[vec * 4 + 2] = rgb[2]; fl[vec * 4 + 3] = a; };
  const ramp = (vec: number, r: Ramp): void => { put(vec, r.light); put(vec + 1, r.mid); put(vec + 2, r.shadow); };
  ramp(0, c.base); ramp(3, c.stripe); ramp(6, c.belly); ramp(9, c.flesh);
  c.dyes.forEach((r, i) => ramp(12 + i * 3, r));
  put(24, c.stroke, c.cov);
  // iris.w: 0, or (when heterochromatic) the second iris packed as 1 + 0xRRGGBB (exact in f32; the shader unpacks it).
  const packed = c.iris2 ? 1 + ((Math.round(lin2s(c.iris2[0]) * 255) << 16) | (Math.round(lin2s(c.iris2[1]) * 255) << 8) | Math.round(lin2s(c.iris2[2]) * 255)) : 0;
  put(25, c.iris, packed);
  u32[26 * 4] = c.pattern.type; u32[26 * 4 + 1] = c.pattern.seed >>> 0; u32[26 * 4 + 2] = c.pattern.flags; u32[26 * 4 + 3] = 0;
  fl[27 * 4] = c.pattern.freq; fl[27 * 4 + 1] = c.pattern.warp; fl[27 * 4 + 2] = c.pattern.threshold; fl[27 * 4 + 3] = c.pattern.bellyWidth;
  if (built) {
    const h = built.pivots.head!;
    const r = (built.dims.hH * built.norm) / 2;
    fl[28 * 4] = h[0]; fl[28 * 4 + 1] = h[1] + r; fl[28 * 4 + 2] = h[2]; fl[28 * 4 + 3] = r;
  } else {
    fl[28 * 4] = 0; fl[28 * 4 + 1] = 0.845; fl[28 * 4 + 2] = 0.015; fl[28 * 4 + 3] = 0.155;
  }
  // line: silhouettePx (base, × the engine's screen-height rule), creasePx, wobbleAmp, shadowSideGain
  fl[29 * 4] = 2.0 * c.outline; fl[29 * 4 + 1] = 1.25 * c.outline; fl[29 * 4 + 2] = 0.25; fl[29 * 4 + 3] = 1.0;
  // fx: hitFlash, paintProgress (1 = painted), wearLevel (patina baseline), frameBreak
  fl[30 * 4] = 0; fl[30 * 4 + 1] = 1; fl[30 * 4 + 2] = c.patina; fl[30 * 4 + 3] = 0;
  // face: pupilScaleL, pupilScaleR, blush, tear (the base pupil size is baked into the mesh: scale 1)
  fl[31 * 4] = 1; fl[31 * 4 + 1] = 1; fl[31 * 4 + 2] = spec.o.mark1 === 'blush' || spec.o.mark2 === 'blush' ? 0.5 : 0; fl[31 * 4 + 3] = 0;
  fl[32 * 4] = 0; fl[32 * 4 + 1] = 0; fl[32 * 4 + 2] = 0; fl[32 * 4 + 3] = 0;
  return buf;
}

const lin2s = (x: number): number => (x <= 0.0031308 ? x * 12.92 : 1.055 * Math.pow(x, 1 / 2.4) - 0.055);
